import React, { useState, useEffect, useCallback, useRef } from "react";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import Input from "@mui/joy/Input";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CircularProgress from "@mui/joy/CircularProgress";
import { gql, useQuery, useMutation } from "@apollo/client";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Dropdown from "@mui/joy/Dropdown";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import Chip from "@mui/joy/Chip";
import Pagination from "../components/Pagination";
import { usePagination } from "../components/usePagination";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import FormControl from "@mui/joy/FormControl";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ChatIcon from "@mui/icons-material/Chat";
import Stack from "@mui/joy/Stack";
import Textarea from "@mui/joy/Textarea";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

// Import role images
import backOfficeOfficialImg from "../../assets/images/back_office_official.png";
import integratorImg from "../../assets/images/integrator.png";
import partnerImg from "../../assets/images/partner.png";
import serviceProviderImg from "../../assets/images/service_provider.png";
import superUserImg from "../../assets/images/super_user.png";
import systemAdministratorImg from "../../assets/images/system_administrator.png";
import systemServiceSupportImg from "../../assets/images/system_service_support.png";
import systemSupportImg from "../../assets/images/system_support.png";

const PROFILE_QUERY = gql`
  query Profile {
    profile {
      id
      firstName
      lastName
      role
    }
  }
`;

const SUPPORT_TICKETS_QUERY = gql`
  query SupportTickets(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filters: SupportTicketFilter
  ) {
    supportTickets(
      first: $first
      after: $after
      last: $last
      before: $before
      filters: $filters
    ) {
      edges {
        node {
          id
          title
          description
          email
          name
          phoneNumber
          createdAt
          status
          submitter {
            id
            firstName
            lastName
            email
            phoneNumber
          }
          assignee {
            id
            firstName
            lastName
            email
            phoneNumber
          }
          messages {
            id
            content
            createdAt
            sender {
              id
              firstName
              lastName
              role
            }
            receiver {
              id
              firstName
              lastName
              role
            }
            read
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

const RESOLVE_SUPPORT_TICKET = gql`
  mutation ResolveSupportTicket($input: ResolveSupportTicketInput!) {
    resolveSupportTicket(input: $input) {
      supportTicket {
        id
        status
      }
    }
  }
`;

const CREATE_MESSAGE = gql`
  mutation CreateMessage($supportTicketId: ID!, $content: String!) {
    createMessage(
      input: { supportTicketId: $supportTicketId, content: $content }
    ) {
      message {
        id
        content
        createdAt
        sender {
          id
          firstName
          lastName
          role
        }
        receiver {
          id
          firstName
          lastName
          role
        }
        read
      }
    }
  }
`;

const MARK_MESSAGES_AS_READ_BY_ID = gql`
  mutation MarkMessagesAsReadById($supportTicketId: ID!) {
    markMessagesAsReadById(input: { supportTicketId: $supportTicketId }) {
      success
      updatedCount
    }
  }
`;

const UNREAD_MESSAGES_COUNT_BY_ID = gql`
  query UnreadMessagesCountById($supportTicketId: ID!) {
    unreadMessagesCountById(supportTicketId: $supportTicketId)
  }
`;

const ASSIGNABLE_PROFILES_QUERY = gql`
  query AssignableProfiles {
    assignableProfiles {
      id
      firstName
      lastName
      email
      phoneNumber
      role
    }
  }
`;

const ASSIGN_SUPPORT_TICKET = gql`
  mutation AssignSupportTicket($supportTicketId: ID!, $assigneeId: ID!) {
    assignSupportTicket(
      input: { supportTicketId: $supportTicketId, assigneeId: $assigneeId }
    ) {
      supportTicket {
        id
        assignee {
          id
          firstName
          lastName
          email
          phoneNumber
        }
      }
    }
  }
`;

const AssigneeModal = ({ open, onClose, ticket }) => {
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(null);

  const { data: officialsData, loading: officialsLoading } = useQuery(
    ASSIGNABLE_PROFILES_QUERY,
    {
      skip: !open,
    }
  );

  const [assignSupportTicket, { loading: assigning }] = useMutation(
    ASSIGN_SUPPORT_TICKET,
    {
      refetchQueries: ["SupportTickets"],
      onCompleted: () => {
        onClose();
      },
      onError: (error) => {
        console.error("Error assigning support ticket:", error);
        alert(`Error: ${error.message}`);
      },
    }
  );

  const assignableProfiles = officialsData?.assignableProfiles || [];

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedAssigneeId(ticket?.assignee?.id || null);
    }
  }, [open, ticket?.assignee?.id]);

  const handleAssign = () => {
    if (selectedAssigneeId && ticket?.id) {
      assignSupportTicket({
        variables: {
          supportTicketId: ticket.id,
          assigneeId: selectedAssigneeId,
        },
      });
    }
  };

  const getDisplayName = (official) => {
    return `${official.firstName || ""} ${official.lastName || ""}`.trim();
  };

  const getRoleImage = (role) => {
    const roleImageMap = {
      BACK_OFFICE_OFFICIAL: backOfficeOfficialImg,
      INTEGRATOR: integratorImg,
      PARTNER: partnerImg,
      SERVICE_PROVIDER: serviceProviderImg,
      SYSTEM_ADMINISTRATOR: systemAdministratorImg,
      SYSTEM_SERVICE_SUPPORT: systemServiceSupportImg,
      SYSTEM_SUPPORT: systemSupportImg,
      SUPER_USER: superUserImg,
    };
    return roleImageMap[role] || backOfficeOfficialImg;
  };

  const getRoleLabel = (role) => {
    const roleLabelMap = {
      BACK_OFFICE_OFFICIAL: "Back Office Official",
      INTEGRATOR: "Integrator",
      PARTNER: "Partner",
      SERVICE_PROVIDER: "Service Provider",
      SYSTEM_ADMINISTRATOR: "System Administrator",
      SYSTEM_SERVICE_SUPPORT: "System Service Support",
      SYSTEM_SUPPORT: "System Support",
      SUPER_USER: "Super User",
    };
    return roleLabelMap[role] || role;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: 500,
          width: "90%",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography level="h4" sx={{ mb: 2 }}>
          Assign Supporter
        </Typography>
        <ModalClose />

        <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
          Select a supporter to assign this support ticket to:
        </Typography>

        {/* Officials List */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            mb: 2,
            maxHeight: "400px",
          }}
        >
          {officialsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size="md" />
            </Box>
          ) : assignableProfiles.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 3,
              }}
            >
              <Typography level="body-sm" color="neutral">
                No assignable supporters available
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {assignableProfiles.map((official) => (
                <Sheet
                  key={official.id}
                  variant={
                    selectedAssigneeId === official.id ? "soft" : "outlined"
                  }
                  sx={{
                    p: 2,
                    borderRadius: "sm",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "background.level2",
                    },
                    ...(selectedAssigneeId === official.id && {
                      borderColor: "primary.500",
                      bgcolor: "primary.softBg",
                    }),
                  }}
                  onClick={() => setSelectedAssigneeId(official.id)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        border: "2px solid",
                        borderColor:
                          selectedAssigneeId === official.id
                            ? "primary.500"
                            : "neutral.300",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {selectedAssigneeId === official.id && (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: "primary.500",
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="body-sm" sx={{ fontWeight: "md" }}>
                        {getDisplayName(official)}
                      </Typography>
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.tertiary" }}
                      >
                        {official.email}
                      </Typography>
                      {official.phoneNumber && (
                        <Typography
                          level="body-xs"
                          sx={{ color: "text.tertiary" }}
                        >
                          {official.phoneNumber}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      component="img"
                      src={getRoleImage(official.role)}
                      alt={getRoleLabel(official.role)}
                      title={getRoleLabel(official.role)}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "sm",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                </Sheet>
              ))}
            </Stack>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            color="neutral"
            onClick={onClose}
            disabled={assigning}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleAssign}
            disabled={!selectedAssigneeId || assigning}
            loading={assigning}
          >
            Assign
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

const ChatBox = ({ open, onClose, ticket, onNewMessage, onMessagesRead }) => {
  const [message, setMessage] = useState("");
  const [localMessages, setLocalMessages] = useState([]);
  const [lastSentMessageId, setLastSentMessageId] = useState(null);
  const messagesEndRef = React.useRef(null);
  const hasMarkedAsRead = useRef(false);
  const markAsReadTimeoutRef = useRef(null);

  // Get current profile
  const { data: profileData } = useQuery(PROFILE_QUERY);
  const currentProfileId = profileData?.profile?.id;

  // Mark messages as read mutation
  const [markMessagesAsRead] = useMutation(MARK_MESSAGES_AS_READ_BY_ID);

  // Debounced function to mark messages as read
  const debouncedMarkAsRead = useCallback(() => {
    if (markAsReadTimeoutRef.current) {
      clearTimeout(markAsReadTimeoutRef.current);
    }

    markAsReadTimeoutRef.current = setTimeout(() => {
      if (open && ticket?.id) {
        markMessagesAsRead({
          variables: { supportTicketId: ticket.id },
        })
          .then(() => {
            if (onMessagesRead) {
              onMessagesRead();
            }
          })
          .catch(console.error);
      }
    }, 2000); // Mark as read after 2 seconds of inactivity
  }, [open, ticket?.id, markMessagesAsRead, onMessagesRead]);

  // Initialize local messages from ticket
  useEffect(() => {
    if (ticket?.messages) {
      setLocalMessages(ticket.messages);
    }
  }, [ticket?.messages]);

  // Mark messages as read when chat is opened (only once per session)
  useEffect(() => {
    if (open && ticket?.id && !hasMarkedAsRead.current) {
      hasMarkedAsRead.current = true;
      markMessagesAsRead({
        variables: { supportTicketId: ticket.id },
      })
        .then(() => {
          // Notify parent component that messages have been read
          if (onMessagesRead) {
            onMessagesRead();
          }
        })
        .catch(console.error);
    }
  }, [open, ticket?.id, onMessagesRead]);

  // Reset the flag when chat is closed
  useEffect(() => {
    if (!open) {
      hasMarkedAsRead.current = false;
      // Clear any pending mark as read timeout
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
        markAsReadTimeoutRef.current = null;
      }
    }
  }, [open]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
      }
    };
  }, []);

  // Subscribe to WebSocket updates for this ticket
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    if (!ticket?.id) return;

    channel.subscribe(
      { channel: "SupportTicketChannel" },
      {
        received: (data) => {
          // Filter by ticket ID
          if (
            data.type === "new_message" &&
            data.support_ticket_id &&
            String(data.support_ticket_id) === String(ticket.id)
          ) {
            // Skip if this is the message we just sent (by ID)
            if (lastSentMessageId && data.message.id === lastSentMessageId) {
              console.log("⏭️ Skipping own message (by ID)");
              return;
            }

            // Also skip if this is our own message (by profile ID)
            if (
              currentProfileId &&
              data.message.sender &&
              String(data.message.sender.id) === String(currentProfileId)
            ) {
              console.log("⏭️ Skipping own message (by profile ID)");
              return;
            }

            setLocalMessages((prevMessages) => {
              // Check if message already exists to avoid duplicates
              const exists = prevMessages.some(
                (msg) => msg.id === data.message.id
              );
              if (exists) {
                console.log("⏭️ Message already exists, skipping");
                return prevMessages;
              }
              return [...prevMessages, data.message];
            });

            // Mark messages as read after a delay when new messages arrive
            if (open) {
              debouncedMarkAsRead();
            }

            // Notify parent component about new message if chatbox is closed
            if (!open && onNewMessage) {
              onNewMessage();
            }
          }
        },
        connected: () => {
          console.log("✅ Connected to SupportTicketChannel");
        },
        disconnected: () => {
          console.log("❌ Disconnected from SupportTicketChannel");
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [ticket?.id, lastSentMessageId, currentProfileId, open, onNewMessage]);

  const [createMessage, { loading: sendingMessage }] = useMutation(
    CREATE_MESSAGE,
    {
      onCompleted: (data) => {
        if (data?.createMessage?.message) {
          // Add the message optimistically to local state
          const newMessage = data.createMessage.message;

          // Store the message ID so we can skip it when it comes via WebSocket
          setLastSentMessageId(newMessage.id);

          setLocalMessages((prevMessages) => {
            // Check if message already exists to avoid duplicates
            const exists = prevMessages.some((msg) => msg.id === newMessage.id);
            if (exists) {
              return prevMessages;
            }
            return [...prevMessages, newMessage];
          });
        }
        setMessage("");
        // Scroll to bottom after sending message
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      },
      onError: (error) => {
        console.error("Error sending message:", error);
        // You could add a toast notification here
      },
    }
  );

  // Auto-scroll to bottom when messages change
  React.useEffect(() => {
    if (localMessages && localMessages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [localMessages]);

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDisplayName = (profile) => {
    return `${profile.firstName || ""} ${profile.lastName || ""}`.trim();
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !ticket?.id) {
      console.log("Cannot send message:", {
        message: message.trim(),
        ticketId: ticket?.id,
      });
      return;
    }

    // Check if the ticket has a submitter (not anonymous)
    if (!ticket.submitter?.id) {
      console.error(
        "No submitter found for ticket. This might be an anonymous ticket."
      );

      // For anonymous tickets, we can't send messages yet
      // You might want to show an error message to the user
      alert(
        "Cannot send messages to anonymous support tickets. The submitter must have a registered account."
      );
      return;
    }

    // Check if the ticket is assigned to someone else
    if (ticket.assignee && ticket.assignee.id !== currentProfileId) {
      console.error(
        "Ticket is assigned to another user. Cannot send messages."
      );

      alert(
        "Cannot send messages to this support ticket. It is assigned to another user."
      );
      return;
    }

    try {
      await createMessage({
        variables: {
          supportTicketId: ticket.id,
          content: message.trim(),
        },
      });
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !sendingMessage) {
      event.preventDefault();
      if (message.trim()) {
        handleSendMessage();
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxWidth: { xs: "95%", sm: 500, md: 600 },
          width: { xs: "95%", sm: 500, md: 600 },
          height: { xs: "85vh", sm: "70vh", md: "60vh" },
          maxHeight: { xs: "85vh", sm: "70vh", md: "60vh" },
          minHeight: { xs: "400px", sm: "450px", md: "500px" },
          display: "flex",
          flexDirection: "column",
          p: 0,
        }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <ChatIcon color="primary" />
            <Typography level="title-lg">Chat</Typography>
          </Box>
          <Typography
            level="body-sm"
            sx={{ color: "text.secondary", fontWeight: "bold" }}
          >
            Description: {ticket?.description}
          </Typography>
          {ticket?.assignee && (
            <Typography
              level="body-xs"
              sx={{ color: "text.tertiary", mt: 0.5 }}
            >
              Assigned to: {getDisplayName(ticket.assignee)}
            </Typography>
          )}
          <ModalClose />
        </Box>

        {/* Messages Container */}
        <Box
          sx={{
            height:
              localMessages && localMessages.length > 5
                ? { xs: "300px", sm: "350px", md: "400px" }
                : "auto",
            maxHeight: { xs: "300px", sm: "350px", md: "400px" },
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            bgcolor: "background.level1",
            minHeight:
              localMessages && localMessages.length > 0
                ? { xs: "150px", sm: "180px", md: "200px" }
                : "auto",
            flex: 1,
          }}
        >
          {!localMessages || localMessages.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: 200,
              }}
            >
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          ) : (
            localMessages.map((msg) => {
              const isOfficialSender =
                msg.sender.role === "SUPER_USER" ||
                msg.sender.role === "BACK_OFFICE_OFFICIAL" ||
                msg.sender.role === "INTEGRATOR" ||
                msg.sender.role === "PARTNER" ||
                msg.sender.role === "SERVICE_PROVIDER" ||
                msg.sender.role === "SYSTEM_ADMINISTRATOR" ||
                msg.sender.role === "SYSTEM_SERVICE_SUPPORT" ||
                msg.sender.role === "SYSTEM_SUPPORT";

              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isOfficialSender ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    {!isOfficialSender && (
                      <Avatar size="sm" sx={{ width: 24, height: 24 }}>
                        {getDisplayName(msg.sender).charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                    <Typography
                      level="body-xs"
                      sx={{ color: "text.secondary" }}
                    >
                      {getDisplayName(msg.sender)}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                      {formatDateTime(msg.createdAt)}
                    </Typography>
                    {isOfficialSender && (
                      <Avatar size="sm" sx={{ width: 24, height: 24 }}>
                        {getDisplayName(msg.sender).charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                  </Box>
                  <Sheet
                    variant="soft"
                    sx={{
                      p: 1.5,
                      borderRadius: "lg",
                      maxWidth: "75%",
                      bgcolor: isOfficialSender
                        ? "primary.softBg"
                        : "background.surface",
                    }}
                  >
                    <Typography level="body-sm" sx={{ whiteSpace: "pre-wrap" }}>
                      {msg.content}
                    </Typography>
                  </Sheet>
                </Box>
              );
            })
          )}
          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box sx={{ p: 2 }}>
          {ticket?.status === "RESOLVED" ? (
            <Box
              sx={{
                p: 2,
                bgcolor: "background.level1",
                borderRadius: "sm",
                textAlign: "center",
              }}
            >
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                This ticket has been resolved. No new messages can be sent.
              </Typography>
            </Box>
          ) : !ticket?.submitter?.id ? (
            <Box
              sx={{
                p: 2,
                bgcolor: "warning.softBg",
                borderRadius: "sm",
                textAlign: "center",
              }}
            >
              <Typography level="body-sm" sx={{ color: "warning.plainColor" }}>
                Cannot send messages to anonymous support tickets. The submitter
                must have a registered account.
              </Typography>
            </Box>
          ) : ticket?.assignee && ticket.assignee.id !== currentProfileId ? (
            <Box
              sx={{
                p: 2,
                bgcolor: "warning.softBg",
                borderRadius: "sm",
                textAlign: "center",
              }}
            >
              <Typography level="body-sm" sx={{ color: "warning.plainColor" }}>
                Cannot send messages to this support ticket. It is assigned to
                another user.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              <Box sx={{ position: "relative" }}>
                <Textarea
                  placeholder="Type your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  minRows={1}
                  maxRows={4}
                  sx={{
                    resize: "none",
                    pr: 6,
                    py: 1.5,
                    fontSize: "0.875rem",
                    "& textarea": {
                      paddingRight: "40px",
                    },
                  }}
                />
                <IconButton
                  size="sm"
                  color="primary"
                  disabled={!message.trim() || sendingMessage}
                  onClick={() => {
                    handleSendMessage();
                  }}
                  sx={{
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                    minHeight: "32px",
                    minWidth: "32px",
                    borderRadius: "50%",
                    bgcolor:
                      message.trim() && !sendingMessage
                        ? "primary.500"
                        : "neutral.300",
                    color:
                      message.trim() && !sendingMessage
                        ? "white"
                        : "neutral.500",
                    "&:hover": {
                      bgcolor:
                        message.trim() && !sendingMessage
                          ? "primary.600"
                          : "neutral.300",
                    },
                    "&:disabled": {
                      bgcolor: "neutral.200",
                      color: "neutral.400",
                    },
                  }}
                >
                  {sendingMessage ? (
                    <CircularProgress size="sm" color="neutral" />
                  ) : (
                    <SendRoundedIcon sx={{ fontSize: "18px" }} />
                  )}
                </IconButton>
              </Box>
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Press Enter to send, Shift+Enter for new line
              </Typography>
            </Stack>
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
};

const Row = (props: { row: any; key?: any }) => {
  const { row } = props;
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);

  // Get current profile for filtering messages
  const { data: profileData } = useQuery(PROFILE_QUERY);
  const currentProfileId = profileData?.profile?.id;
  const currentProfileRole = profileData?.profile?.role;

  // Sound notification function
  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio("/sounds/notification.mp3");
      audio.volume = 1.0; // Adjust volume (0.0 to 1.0)
      audio.play().catch((error) => {
        console.log("Could not play notification sound:", error);
      });
    } catch (error) {
      console.log("Error creating audio:", error);
    }
  }, []);

  // Query for unread messages count - only for PENDING tickets
  const { data: unreadData, refetch: refetchUnreadCount } = useQuery(
    UNREAD_MESSAGES_COUNT_BY_ID,
    {
      variables: { supportTicketId: row.id },
      skip: !row.id || row.status !== "PENDING", // Only query for PENDING tickets
      pollInterval: 5000, // Poll every 5 seconds
      notifyOnNetworkStatusChange: true,
    }
  );

  // Update unread count from query data
  useEffect(() => {
    if (unreadData?.unreadMessagesCountById !== undefined) {
      setUnreadCount(unreadData.unreadMessagesCountById);
    }
  }, [unreadData?.unreadMessagesCountById]);

  // Play sound notification when unread count increases
  useEffect(() => {
    if (unreadCount > previousUnreadCount && unreadCount > 0) {
      playNotificationSound();
    }
    setPreviousUnreadCount(unreadCount);
  }, [unreadCount, previousUnreadCount, playNotificationSound]);

  // Subscribe to WebSocket updates for this ticket
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    // Only subscribe to WebSocket for PENDING tickets
    if (!row?.id || row.status !== "PENDING") return;

    channel.subscribe(
      { channel: "SupportTicketChannel" },
      {
        received: (wsData) => {
          // Filter by ticket ID
          if (
            wsData.type === "new_message" &&
            wsData.support_ticket_id &&
            String(wsData.support_ticket_id) === String(row.id)
          ) {
            // Check if this is a message from submitter to support team
            if (
              wsData.message.sender &&
              wsData.message.receiver &&
              wsData.message.sender.id !== wsData.message.receiver.id
            ) {
              // Only play sound if this is NOT a message from the current support team member
              // (i.e., it's a message from submitter to support team)
              if (
                currentProfileId &&
                wsData.message.sender.id !== currentProfileId
              ) {
                // Immediately update unread count locally for instant UI update
                setUnreadCount((prevCount) => prevCount + 1);

                // Play sound notification immediately when message arrives
                playNotificationSound();
              }
            }
          }
        },
        connected: () => {
          console.log(
            "✅ Connected to SupportTicketChannel for ticket",
            row.id
          );
        },
        disconnected: () => {
          console.log(
            "❌ Disconnected from SupportTicketChannel for ticket",
            row.id
          );
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [row?.id, row?.status, refetchUnreadCount]);

  const [resolveTicket, { loading: resolving }] = useMutation(
    RESOLVE_SUPPORT_TICKET,
    {
      variables: { input: { id: row.id } },
      refetchQueries: ["SupportTickets"],
    }
  );

  const formatDateTime = (dateString) => {
    if (!dateString) return "Not provided";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSubmitterName = (submitter) => {
    if (!submitter) return row.name || "Anonymous";
    return (
      `${submitter.firstName || ""} ${submitter.lastName || ""}`.trim() ||
      "Unknown"
    );
  };

  const getSubmitterEmail = (submitter, email) => {
    if (submitter?.email) return submitter.email;
    if (email) return email;
    return "Not provided";
  };

  return (
    <React.Fragment>
      <tr>
        <td>
          <IconButton
            aria-label="expand row"
            variant="plain"
            color="neutral"
            size="sm"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
        <td>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar size="sm" alt={getSubmitterName(row.submitter)} />
            <div>
              <Typography level="body-xs">
                {getSubmitterName(row.submitter)}
              </Typography>
              <Typography
                level="body-xs"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "180px",
                }}
              >
                {getSubmitterEmail(row.submitter, row.email)}
              </Typography>
            </div>
          </Box>
        </td>
        <td>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {row.assignee ? (
              <>
                <Avatar
                  size="sm"
                  alt={`${row.assignee.firstName || ""} ${
                    row.assignee.lastName || ""
                  }`.trim()}
                />
                <div>
                  <Typography level="body-xs">
                    {`${row.assignee.firstName || ""} ${
                      row.assignee.lastName || ""
                    }`.trim()}
                  </Typography>
                  <Typography
                    level="body-xs"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "180px",
                      color: "text.tertiary",
                    }}
                  >
                    {row.assignee.role}
                  </Typography>
                </div>
              </>
            ) : (
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Unassigned
              </Typography>
            )}
          </Box>
        </td>
        <td>
          <Typography level="body-xs">{row.title}</Typography>
        </td>
        <td>
          <Chip
            variant="soft"
            size="sm"
            color={row.status === "PENDING" ? "warning" : "success"}
          >
            {row.status === "PENDING" ? "PENDING" : "RESOLVED"}
          </Chip>
        </td>
        <td>
          <Typography level="body-xs">
            {formatDateTime(row.createdAt)}
          </Typography>
        </td>
        <td>
          {row.status === "PENDING" && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Dropdown>
                <MenuButton
                  slots={{ root: IconButton }}
                  slotProps={{
                    root: { variant: "plain", color: "neutral", size: "sm" },
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <MoreHorizRoundedIcon />
                    {unreadCount > 0 && row.status === "PENDING" && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -2,
                          right: -15,
                          minWidth: 16,
                          height: 16,
                          borderRadius: "50%",
                          bgcolor: "danger.500",
                          border: "2px solid white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          fontWeight: "bold",
                          color: "white",
                        }}
                      >
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </Box>
                    )}
                  </Box>
                </MenuButton>
                <Menu size="sm" sx={{ minWidth: 140 }}>
                  <MenuItem
                    onClick={() => {
                      setChatOpen(true);
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      Chat with submitter
                      {unreadCount > 0 && row.status === "PENDING" && (
                        <Chip size="sm" color="danger" variant="solid">
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </Chip>
                      )}
                    </Box>
                  </MenuItem>
                  {currentProfileRole === "SUPER_USER" && (
                    <MenuItem
                      onClick={() => {
                        setAssigneeModalOpen(true);
                      }}
                    >
                      Assign supporter
                    </MenuItem>
                  )}
                  <MenuItem
                    disabled={resolving}
                    onClick={() => resolveTicket()}
                  >
                    Mark as resolved
                  </MenuItem>
                </Menu>
              </Dropdown>
            </Box>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6}>
            <Table size="sm">
              <tbody>
                <tr>
                  <td>
                    <Typography level="body-xs">Phone number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.phoneNumber ||
                        row.submitter?.phoneNumber ||
                        "Not provided"}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Email</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {getSubmitterEmail(row.submitter, row.email)}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Assignee</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.assignee
                        ? `${row.assignee.firstName || ""} ${
                            row.assignee.lastName || ""
                          }`.trim()
                        : "Unassigned"}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Description</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.description || "Not provided"}
                    </Typography>
                  </td>
                </tr>
              </tbody>
            </Table>
          </td>
        </tr>
      )}
      <ChatBox
        open={chatOpen}
        onClose={() => {
          setChatOpen(false);
        }}
        ticket={row}
        onNewMessage={() => {}}
        onMessagesRead={() => {
          setUnreadCount(0);
          refetchUnreadCount();
        }}
      />
      <AssigneeModal
        open={assigneeModalOpen}
        onClose={() => {
          setAssigneeModalOpen(false);
        }}
        ticket={row}
      />
    </React.Fragment>
  );
};

const formatDateForAPI = (date: string | undefined) => {
  if (!date) return undefined;
  return new Date(date).toISOString();
};

const SupportTicketsTable = ({
  submitter,
  content,
  status,
  startDate,
  endDate,
}: {
  submitter: string;
  content: string;
  status: string;
  startDate: string | undefined;
  endDate: string | undefined;
}) => {
  const [pageSize] = useState(10);
  const { data, loading, error, fetchMore, refetch } = useQuery(
    SUPPORT_TICKETS_QUERY,
    {
      variables: {
        first: pageSize,
        filters: {
          submitter: submitter || undefined,
          content: content || undefined,
          status: status || undefined,
          dateRange: {
            start: formatDateForAPI(startDate),
            end: formatDateForAPI(endDate),
          },
        },
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  const {
    pageInfo,
    isLoadingMore,
    handlePageChange,
    canGoNext,
    canGoPrevious,
  } = usePagination({
    pageSize,
    data,
    fetchMore,
    getPageInfo: (data) => data.supportTickets.pageInfo,
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) return prev;
      return {
        supportTickets: {
          ...fetchMoreResult.supportTickets,
          edges: [...fetchMoreResult.supportTickets.edges],
        },
      };
    },
  });

  // Setup WebSocket connection for ticket updates
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (wsData.type === "support_ticket_updated") {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography level="body-lg" color="danger">
          Error loading data: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
              <th style={{ width: 200, padding: "12px 6px" }}>Submitter</th>
              <th style={{ width: 200, padding: "12px 6px" }}>Assignee</th>
              <th style={{ width: 200, padding: "12px 6px" }}>Title</th>
              <th style={{ width: 120, padding: "12px 6px" }}>Status</th>
              <th style={{ width: 120, padding: "12px 6px" }}>Created At</th>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading || isLoadingMore ? (
              <tr>
                <td colSpan={7}>
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                </td>
              </tr>
            ) : data?.supportTickets?.edges?.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 4,
                    }}
                  >
                    <Typography level="body-lg" color="neutral">
                      {submitter || content || status || startDate || endDate
                        ? "No support tickets found matching your filters."
                        : "No support tickets found."}
                    </Typography>
                  </Box>
                </td>
              </tr>
            ) : (
              data?.supportTickets?.edges?.map(({ node: row }) => (
                <Row key={row.id} row={row} />
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      <Pagination
        pageInfo={pageInfo}
        isLoadingMore={isLoadingMore}
        onPageChange={handlePageChange}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
      />
    </React.Fragment>
  );
};

const SupportTickets = () => {
  const [submitter, setSubmitter] = useState("");
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [debouncedSubmitter, setDebouncedSubmitter] = useState("");
  const [debouncedContent, setDebouncedContent] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSubmitter(submitter);
    }, 300);

    return () => clearTimeout(timer);
  }, [submitter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContent(content);
    }, 300);

    return () => clearTimeout(timer);
  }, [content]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Support Tickets
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        <FormControl sx={{ flex: 1, minWidth: 220 }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Search for submitter
          </Typography>
          <Input
            placeholder="Search by submitter name or email..."
            startDecorator={<SearchIcon />}
            endDecorator={
              submitter && (
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() => setSubmitter("")}
                >
                  <ClearIcon />
                </IconButton>
              )
            }
            value={submitter}
            onChange={(event) => setSubmitter(event.target.value)}
          />
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: 220 }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Search by content
          </Typography>
          <Input
            placeholder="Search by title or description..."
            startDecorator={<SearchIcon />}
            endDecorator={
              content && (
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() => setContent("")}
                >
                  <ClearIcon />
                </IconButton>
              )
            }
            value={content}
            onChange={(event) => setContent(event.target.value)}
          />
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: 160 }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Status
          </Typography>
          <Select
            placeholder="All Status"
            value={status}
            onChange={(_, newValue) => setStatus(newValue || "")}
          >
            <Option value="">All Status</Option>
            <Option value="PENDING">Pending</Option>
            <Option value="RESOLVED">Resolved</Option>
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: 150 }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Start Date
          </Typography>
          <Input
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </FormControl>

        <FormControl sx={{ flex: 1, minWidth: 150 }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            End Date
          </Typography>
          <Input
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </FormControl>
      </Box>

      <SupportTicketsTable
        submitter={debouncedSubmitter}
        content={debouncedContent}
        status={status}
        startDate={startDate || undefined}
        endDate={endDate || undefined}
      />
    </>
  );
};

export default SupportTickets;
